import { useState } from 'react'
import { createRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { MessageSquare } from 'lucide-react'
import { commentsRoute } from '@/ui/pages/management/comments'
import { useGetAllInvestigations } from '@/application/services/investigation.service'
import { useGetCommentsSummary } from '@/application/services/comment.service'
import { Button } from '@/ui/components/common/Button'

export const commentsIndexRoute = createRoute({
  getParentRoute: () => commentsRoute,
  path: '/',
  component: CommentsIndexPage,
})

const PAGE_SIZE = 10

/**
 * Cross-investigation comments overview: one row per investigation with its
 * total and pending comment counts, and a "Modérer" action linking to that
 * investigation's moderation page. Mirrors the investigations list's
 * client-side pagination — the dataset is small enough that a dedicated
 * paginated endpoint would be over-engineering.
 */
export function CommentsIndexPage() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)

  const { data: investigations, isLoading: isLoadingInvestigations } = useGetAllInvestigations()
  const { data: summary, isLoading: isLoadingSummary } = useGetCommentsSummary()

  const isLoading = isLoadingInvestigations || isLoadingSummary
  const summaryByInvestigationId = new Map(
    (summary ?? []).map((summaryRow) => [summaryRow.investigationId, summaryRow]),
  )

  const rows = (investigations ?? []).map((investigation) => ({
    investigation,
    total: summaryByInvestigationId.get(investigation.id)?.total ?? 0,
    pending: summaryByInvestigationId.get(investigation.id)?.pending ?? 0,
  }))

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const paginated = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_unused, index) => (
            <div key={index} className="h-14 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>{t('admin.comments_list_empty')}</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('admin.comments_col_investigation')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('admin.comments_col_total')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('admin.comments_col_pending')}
                </th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                  {t('admin.comments_moderate_button')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginated.map(({ investigation, total, pending }) => (
                <tr key={investigation.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium">{investigation.name}</td>
                  <td className="px-4 py-3">{total}</td>
                  <td className="px-4 py-3">
                    {pending > 0 ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-500/10 text-amber-600 border border-amber-500/20">
                        {pending}
                      </span>
                    ) : (
                      0
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {pending > 0 ? (
                      <Link
                        to="/manage/comments/$investigationId"
                        params={{ investigationId: investigation.id }}
                      >
                        <Button size="sm" variant="outline">
                          {t('admin.comments_moderate_button')}
                        </Button>
                      </Link>
                    ) : (
                      <Button size="sm" variant="outline" disabled>
                        {t('admin.comments_moderate_button')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{t('admin.pagination_info', { page, total: totalPages })}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              {t('common.page_prev')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              {t('common.page_next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
